import React from 'react';

import {useRampFlagMediaPointcloudPotreeRenderer} from '../util/rampFeatureFlags/rampFeatureFlags';
import {
  type MediaPanelCardControl,
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from './MediaCard';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {babylonPointCloudRenderer} from './PointCloudCard/babylonRenderer';
import {PointCloudViewer} from './PointCloudCard/PointCloudViewer';
import {potreePointCloudRenderer} from './PointCloudCard/potreeRenderer';
import {type PointCloudRgb, type PointCloudRgba} from './PointCloudCard/types';
import {usePointCloudScene} from './PointCloudCard/usePointCloudScene';

export interface Media3DProps {
  width: number;
  height: number;
  url: string;
  blob: Blob;
  controls?: MediaPanelCardControl;
  mediaKey: string;
  mediaPanelRefId: string;
  backgroundColor?: PointCloudRgba;
  pointColor?: PointCloudRgb;
  useRightHandedSystem?: boolean;
  tileMedia: TileMedia | null;
  tileMediaSpec: TileMediaSpec | undefined;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;
}

/**
 * Workspace Object3D point-cloud card. Parse/preprocess live in
 * {@link usePointCloudScene}; Babylon or Potree draws through
 * {@link PointCloudViewer} based on the entity gate
 * `media_pointcloud_potree_renderer`.
 */
export const PointCloud = ({
  width,
  height,
  blob,
  mediaKey,
  mediaPanelRefId,
  controls,
  backgroundColor,
  pointColor,
  useRightHandedSystem,
  tileMedia,
  tileMediaSpec,
  getMediaTracking,
}: Media3DProps) => {
  const entityName = tileMediaSpec?.runSignature.entityName;
  const usePotree = useRampFlagMediaPointcloudPotreeRenderer(entityName);
  const renderer = usePotree
    ? potreePointCloudRenderer
    : babylonPointCloudRenderer;

  const {scene, sourcePointCount, loadErrorMessage} = usePointCloudScene({
    blob,
    mediaKey,
    mediaPanelRefId,
    controls,
    pointColor,
  });

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  if (loadErrorMessage) {
    console.log(`error ${loadErrorMessage}`);
    return <div className="card3d">Error: {loadErrorMessage}</div>;
  }

  if (scene == null) {
    return <div className="media-card group" />;
  }

  return (
    <PointCloudViewer
      scene={scene}
      width={width}
      height={height}
      renderer={renderer}
      sourcePointCount={sourcePointCount}
      backgroundColor={backgroundColor}
      pointColor={pointColor}
      useRightHandedSystem={useRightHandedSystem}
      cameraControl={controls?.cameraControl}
      trackSuccess={trackSuccess}
      trackError={trackError}
    />
  );
};

export default PointCloud;
